import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Save, ArrowLeft, Upload } from 'lucide-react';
import { useAuth } from '../../context/AdminContext';
import {
  useLazyGetItemsQuery,
  useCreateItemMutation,
  useUpdateItemMutation
} from '../../api/businessApi';
import Swal from 'sweetalert2';

// Simple rich text editor component
const SimpleRichTextEditor = ({ value, onChange, placeholder }) => {
  const [content, setContent] = useState(value || '');

  useEffect(() => {
    setContent(value || '');
  }, [value]);

  const handleInputChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    onChange(newContent);
  };

  const insertTag = (tag) => {
    const textarea = document.getElementById('rich-editor');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);

    let newText = '';
    switch (tag) {
      case 'bold':
        newText = `**${selectedText}**`;
        break;
      case 'italic':
        newText = `*${selectedText}*`;
        break;
      case 'heading':
        newText = `## ${selectedText}`;
        break;
      case 'list':
        newText = `\n- ${selectedText}`;
        break;
      default:
        newText = selectedText;
    }

    const newContent = content.substring(0, start) + newText + content.substring(end);
    setContent(newContent);
    onChange(newContent);

    // Reset cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + newText.length, start + newText.length);
    }, 0);
  };

  return (
    <div className="border border-gray-300 rounded-md">
      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-300 p-2 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => insertTag('bold')}
          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100 font-bold"
          title="Bold"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => insertTag('italic')}
          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100 italic"
          title="Italic"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => insertTag('heading')}
          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
          title="Heading"
        >
          H
        </button>
        <button
          type="button"
          onClick={() => insertTag('list')}
          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
          title="List"
        >
          •
        </button>
      </div>

      {/* Textarea */}
      <textarea
        id="rich-editor"
        value={content}
        onChange={handleInputChange}
        placeholder={placeholder || 'Enter content...'}
        className="w-full min-h-[200px] p-3 border-0 focus:outline-none resize-none"
        style={{ fontFamily: 'monospace' }}
      />

      {/* Preview */}
      <div className="bg-gray-50 border-t border-gray-300 p-3">
        <div className="text-xs text-gray-500 mb-2">Preview:</div>
        <div
          className="text-sm prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{
            __html: content
              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              .replace(/\*(.*?)\*/g, '<em>$1</em>')
              .replace(/## (.*)/g, '<h3 class="text-lg font-semibold mb-2">$1</h3>')
              .replace(/^- (.*)/gm, '<li class="ml-4">• $1</li>')
              .replace(/\n/g, '<br>')
          }}
        />
      </div>
    </div>
  );
};

const ItemFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEditing = Boolean(id);

  const [description, setDescription] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: {
      title: '',
      subtitle: '',
      description: '',
      image: ''
    }
  });

  // RTK Query hooks
  const [getItems] = useLazyGetItemsQuery();
  const [createItem, { isLoading: createLoading }] = useCreateItemMutation();
  const [updateItem, { isLoading: updateLoading }] = useUpdateItemMutation();

  // Check authentication
  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
    }
  }, [user, navigate]);

  // Load item data if editing
  useEffect(() => {
    if (isEditing && id) {
      const loadItemData = async () => {
        setIsLoadingData(true);
        try {
          const result = await getItems();
          const items = result.data?.items || result.data || [];
          const item = items.find(item => item.id === Number(id));
          
          if (item) {
            setValue('title', item.title || '');
            setValue('subtitle', item.subtitle || '');
            setValue('description', item.description || '');
            setValue('image', item.image || '');
            setDescription(item.description || '');
            if (item.image) {
              setImagePreview(item.image);
            }
          } else {
            console.error('Item not found with ID:', id);
            navigate('/admin/dashboard?tab=items');
          }
        } catch (error) {
          console.error('Error loading item data:', error);
        } finally {
          setIsLoadingData(false);
        }
      };

      loadItemData();
    }
  }, [id, isEditing, getItems, setValue, navigate]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImagePreview(base64String);
        setValue('image', base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDescriptionChange = (value) => {
    setDescription(value);
    setValue('description', value);
  };

  const onFormSubmit = async (data) => {
    const formData = {
      ...data,
      description: description
    };

    try {
      if (isEditing) {
        await updateItem({ id: Number(id), ...formData }).unwrap();
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Item updated successfully!',
        });
      } else {
        await createItem(formData).unwrap();
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Item created successfully!',
        });
      }
      navigate('/admin/dashboard?tab=items');
    } catch (error) {
      console.error('Error saving item:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.data?.message || error.message || 'Failed to save item',
      });
    }
  };

  const handleCancel = () => {
    navigate('/admin/dashboard?tab=items');
  };

  if (isLoadingData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-gray-500">Loading item data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-3xl font-bold text-gray-800">
            {isEditing ? 'Edit Item' : 'Create New Item'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-8">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title *
          </label>
          <input
            type="text"
            {...register('title', { required: 'Title is required' })}
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter item title"
          />
          {errors.title && (
            <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
          )}
        </div>

        {/* Subtitle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subtitle
          </label>
          <input
            type="text"
            {...register('subtitle')}
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter item subtitle (optional)"
          />
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Image
          </label>
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <label className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md cursor-pointer transition-colors">
                <Upload className="w-4 h-4 mr-2" />
                Choose Image
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
              <span className="text-sm text-gray-600">
                {imageFile ? imageFile.name : 'No file chosen'}
              </span>
            </div>

            {imagePreview && (
              <div className="mt-4">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-48 h-48 object-cover rounded-md border border-gray-300"
                />
              </div>
            )}
          </div>
        </div>

        {/* Description with Rich Text Editor */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description *
          </label>
          <SimpleRichTextEditor
            value={description}
            onChange={handleDescriptionChange}
            placeholder="Enter item description..."
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-8 border-t">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createLoading || updateLoading}
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {createLoading || updateLoading ? 'Saving...' : (isEditing ? 'Update Item' : 'Create Item')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ItemFormPage;
